/**
 * Quick Assessment question definitions.
 * ~12 questions covering all 33 frameworks.
 * Used on the marketing site (lead magnet) and later by the portal's Assessment Wizard.
 */
export const ASSESSMENT_QUESTIONS = [
    {
        id: 'company-location',
        question: 'Where is your company registered?',
        type: 'single',
        options: [
            { label: 'Saudi Arabia / GCC', value: 'ksa' },
            { label: 'European Union', value: 'eu' },
            { label: 'United Kingdom', value: 'uk' },
            { label: 'United States', value: 'us' },
            { label: 'Brazil', value: 'br' },
            { label: 'Other', value: 'other' },
        ],
        frameworkTriggers: {
            ndmo: 'mandatory',
            pdpl: 'mandatory',
            'nca-ecc': 'recommended',
            gdpr: 'mandatory',
            'uk-gdpr': 'mandatory',
            ccpa: 'recommended',
            lgpd: 'mandatory',
        },
    },
    {
        id: 'customer-locations',
        question: 'Where do your customers or users reside?',
        type: 'multi',
        options: [
            { label: 'Saudi Arabia / GCC', value: 'ksa' },
            { label: 'European Union', value: 'eu' },
            { label: 'United Kingdom', value: 'uk' },
            { label: 'United States', value: 'us' },
            { label: 'Brazil', value: 'br' },
            { label: 'Other', value: 'other' },
        ],
        frameworkTriggers: {
            pdpl: 'mandatory',
            gdpr: 'mandatory',
            'uk-gdpr': 'mandatory',
            ccpa: 'mandatory',
            lgpd: 'mandatory',
            hipaa: 'recommended',
        },
    },
    {
        id: 'industry',
        question: 'What industry are you in?',
        type: 'single',
        options: [
            { label: 'Healthcare', value: 'healthcare' },
            { label: 'Life Sciences / Pharma', value: 'life-sciences' },
            { label: 'Financial Services', value: 'financial' },
            { label: 'Technology / SaaS', value: 'tech' },
            { label: 'Manufacturing', value: 'manufacturing' },
            { label: 'Government / Defense', value: 'gov-defense' },
            { label: 'Other', value: 'other' },
        ],
        frameworkTriggers: {
            hipaa: 'mandatory',
            fda: 'recommended',
            'eu-mdr': 'recommended',
            gxp: 'mandatory',
            'ich-gcp': 'recommended',
            dora: 'mandatory',
            sox: 'recommended',
            cmmc: 'mandatory',
        },
    },
    {
        id: 'personal-data',
        question: 'Do you process personal data?',
        type: 'boolean',
        frameworkTriggers: {
            gdpr: 'mandatory',
            pdpl: 'mandatory',
            ccpa: 'mandatory',
            'uk-gdpr': 'mandatory',
            lgpd: 'mandatory',
            'iso-27701': 'recommended',
        },
    },
    {
        id: 'health-data',
        question: 'Do you process health information or provide healthcare services?',
        type: 'boolean',
        frameworkTriggers: {
            hipaa: 'mandatory',
            'eu-mdr': 'recommended',
            mhra: 'recommended',
            'iso-13485': 'recommended',
        },
    },
    {
        id: 'fda-regulated',
        question: 'Do you handle FDA-regulated products, clinical trials, or pharmaceutical data?',
        type: 'boolean',
        frameworkTriggers: {
            fda: 'mandatory',
            gxp: 'mandatory',
            'ich-gcp': 'mandatory',
            'iso-13485': 'mandatory',
        },
    },
    {
        id: 'payment-cards',
        question: 'Do you handle payment card data?',
        type: 'boolean',
        frameworkTriggers: {
            'pci-dss': 'mandatory',
        },
    },
    {
        id: 'gov-defense',
        question: 'Do you work with US government or defense contracts?',
        type: 'boolean',
        frameworkTriggers: {
            cmmc: 'mandatory',
            cybersecurity: 'mandatory',
            'nist-csf': 'recommended',
        },
    },
    {
        id: 'ai-systems',
        question: 'Are you developing or deploying AI/ML systems?',
        type: 'boolean',
        frameworkTriggers: {
            'iso-42001': 'mandatory',
            'nist-ai-rmf': 'recommended',
        },
    },
    {
        id: 'financial-critical',
        question: 'Do you operate in financial services or critical infrastructure?',
        type: 'boolean',
        frameworkTriggers: {
            dora: 'mandatory',
            sox: 'mandatory',
            'soc-2': 'recommended',
            'iso-27001': 'recommended',
            'iso-22301': 'recommended',
        },
    },
    {
        id: 'employee-count',
        question: 'How many employees do you have?',
        type: 'single',
        options: [
            { label: '1-10', value: '1-10' },
            { label: '11-50', value: '11-50' },
            { label: '51-200', value: '51-200' },
            { label: '200+', value: '200+' },
        ],
        frameworkTriggers: {},
    },
    {
        id: 'fundraising',
        question: 'Are you preparing for fundraising, investor due diligence, or external audits?',
        type: 'boolean',
        frameworkTriggers: {
            'soc-2': 'recommended',
            'iso-27001': 'recommended',
        },
    },
];
/**
 * Determine the recommended tier based on framework count and company size.
 */
export function recommendTier(frameworkCount, employeeCount) {
    if (frameworkCount === 0)
        return 'free';
    if (frameworkCount <= 3 && (employeeCount === '1-10' || employeeCount === '11-50'))
        return 'starter';
    if (frameworkCount <= 10)
        return 'pro';
    return 'enterprise';
}
/**
 * Determine the recommended region based on company location answer.
 */
export function recommendRegion(companyLocation) {
    switch (companyLocation) {
        case 'ksa': return 'ksa';
        case 'eu':
        case 'uk': return 'eu';
        case 'us': return 'us';
        case 'br': return 'us';
        default: return 'us';
    }
}
/**
 * Score assessment answers and return framework recommendations.
 * This is a simplified scoring engine for the marketing site.
 * The portal will have a more detailed scoring system.
 */
export function scoreAssessment(answers) {
    const frameworkScores = {};
    for (const question of ASSESSMENT_QUESTIONS) {
        const answer = answers[question.id];
        if (answer === null || answer === undefined)
            continue;
        // Determine which triggers fire based on answer
        for (const [fwId, confidence] of Object.entries(question.frameworkTriggers)) {
            let triggers = false;
            if (question.type === 'boolean') {
                triggers = answer === true;
            }
            else if (question.type === 'single') {
                // Location-based triggers: check if the answer matches the framework's region
                if (question.id === 'company-location') {
                    triggers = shouldTriggerForLocation(fwId, answer);
                }
                else if (question.id === 'industry') {
                    triggers = shouldTriggerForIndustry(fwId, answer);
                }
                else {
                    triggers = true;
                }
            }
            else if (question.type === 'multi' && Array.isArray(answer)) {
                if (question.id === 'customer-locations') {
                    triggers = shouldTriggerForCustomerLocations(fwId, answer);
                }
                else {
                    triggers = answer.length > 0;
                }
            }
            if (triggers && confidence) {
                const existing = frameworkScores[fwId];
                if (!existing || confidenceRank(confidence) > confidenceRank(existing.confidence)) {
                    frameworkScores[fwId] = {
                        confidence,
                        reason: generateReason(fwId, question, answer),
                    };
                }
            }
        }
    }
    const frameworks = Object.entries(frameworkScores).map(([id, data]) => ({
        id: id,
        ...data,
    }));
    // Sort: mandatory first, then recommended, then optional
    frameworks.sort((a, b) => confidenceRank(b.confidence) - confidenceRank(a.confidence));
    const companyLocation = answers['company-location'] || 'us';
    const employeeCount = answers['employee-count'] || '1-10';
    return {
        frameworks,
        recommendedTier: recommendTier(frameworks.filter((f) => f.confidence === 'mandatory').length, employeeCount),
        recommendedRegion: recommendRegion(companyLocation),
    };
}
function confidenceRank(c) {
    return c === 'mandatory' ? 3 : c === 'recommended' ? 2 : 1;
}
function shouldTriggerForLocation(fwId, location) {
    const locationMap = {
        ksa: ['ndmo', 'pdpl', 'nca-ecc'],
        eu: ['gdpr'],
        uk: ['uk-gdpr'],
        us: ['ccpa'],
        br: ['lgpd'],
    };
    return locationMap[location]?.includes(fwId) ?? false;
}
function shouldTriggerForCustomerLocations(fwId, locations) {
    const locationMap = {
        ksa: ['pdpl'],
        eu: ['gdpr'],
        uk: ['uk-gdpr'],
        us: ['ccpa', 'hipaa'],
        br: ['lgpd'],
    };
    return locations.some((loc) => locationMap[loc]?.includes(fwId));
}
function shouldTriggerForIndustry(fwId, industry) {
    const industryMap = {
        healthcare: ['hipaa', 'eu-mdr'],
        'life-sciences': ['fda', 'gxp', 'ich-gcp', 'iso-13485'],
        financial: ['dora', 'sox', 'soc-2'],
        'gov-defense': ['cmmc'],
        tech: [],
        manufacturing: ['iso-13485'],
    };
    return industryMap[industry]?.includes(fwId) ?? false;
}
function generateReason(fwId, question, _answer) {
    const reasonMap = {
        ndmo: { 'company-location': 'Your company is registered in Saudi Arabia — NDMO compliance is mandatory' },
        pdpl: { 'company-location': 'Saudi-registered companies must comply with PDPL for personal data', 'personal-data': 'You process personal data subject to PDPL' },
        gdpr: { 'company-location': 'EU-based companies must comply with GDPR', 'customer-locations': 'You have EU-based customers — GDPR applies', 'personal-data': 'You process personal data of EU residents' },
        'uk-gdpr': { 'company-location': 'UK companies must comply with UK GDPR', 'customer-locations': 'You have UK-based customers — UK GDPR applies' },
        ccpa: { 'company-location': 'US-based companies may need CCPA compliance for California consumers', 'customer-locations': 'You have US-based customers — CCPA may apply' },
        lgpd: { 'company-location': 'Brazilian companies must comply with LGPD', 'customer-locations': 'You have Brazilian customers — LGPD applies' },
        hipaa: { 'health-data': 'You handle health information — HIPAA compliance is required', 'industry': 'Healthcare industry requires HIPAA compliance' },
        fda: { 'fda-regulated': 'You handle FDA-regulated products — 21 CFR Part 11 applies', 'industry': 'Life sciences industry typically requires FDA compliance' },
        'pci-dss': { 'payment-cards': 'You handle payment card data — PCI DSS compliance is mandatory' },
        cmmc: { 'gov-defense': 'US government/defense contracts require CMMC certification', 'industry': 'Government/defense sector requires CMMC' },
        'iso-42001': { 'ai-systems': 'You deploy AI systems — ISO 42001 provides governance structure' },
        'nist-ai-rmf': { 'ai-systems': 'AI system development benefits from NIST AI Risk Management Framework' },
        dora: { 'financial-critical': 'Financial services must comply with DORA for digital operational resilience', 'industry': 'Financial services industry requires DORA compliance' },
        sox: { 'financial-critical': 'Financial reporting obligations under Sarbanes-Oxley Act' },
        'soc-2': { 'financial-critical': 'SOC 2 demonstrates trust service criteria compliance', 'fundraising': 'Investors often require SOC 2 reports' },
        'iso-27001': { 'financial-critical': 'ISO 27001 provides comprehensive security management', 'fundraising': 'ISO 27001 certification strengthens investor confidence' },
    };
    return reasonMap[fwId]?.[question.id] || `Based on your ${question.question.toLowerCase().replace('?', '')}`;
}
//# sourceMappingURL=assessment.js.map